import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@powersync/react';
import { useClinicCtx } from '@/app/state';
import { Button, Card, Empty, Field, Input, Modal, Pill, Select, cx } from '@/components/ui';
import { adjustStock, listSuppliers, receiveGoods, saveDrug, saveSupplier, type AdjustKind, type DrugInput, type ReceiptLine } from '@/features/pharmacy/repo';
import { batchesForDrug, daysToExpiry, expiringBatches, stockList, stockValue, type BatchStock, type DrugStock } from '@/features/pharmacy/stock';
import { parseDrugCsv, type ParsedDrug } from '@/features/pharmacy/import';
import { formatDate, formatMMK, toMyanmarDigits } from '@/lib/myanmar/digits';

type Tab = 'drugs' | 'expiring' | 'low';

export function StockPage() {
  const { t, i18n } = useTranslation();
  const my = i18n.language === 'my';
  const ctx = useClinicCtx();
  const [tab, setTab] = useState<Tab>('drugs');
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<DrugStock[]>([]);
  const [expiring, setExpiring] = useState<Awaited<ReturnType<typeof expiringBatches>>>([]);
  const [value, setValue] = useState(0);
  const [editing, setEditing] = useState<DrugInput | null>(null);
  const [receiving, setReceiving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [adjusting, setAdjusting] = useState<DrugStock | null>(null);

  const { data: tick } = useQuery('select count(*) as n, max(moved_at) as m from stock_movements where clinic_id = ?', [ctx.clinicId]);
  const reload = useCallback(async () => {
    setRows(await stockList(ctx.db, ctx.clinicId, { search: q, lowOnly: tab === 'low' }));
    setExpiring(await expiringBatches(ctx.db, ctx.clinicId));
    setValue(await stockValue(ctx.db, ctx.clinicId));
  }, [ctx.db, ctx.clinicId, q, tab]);
  useEffect(() => { void reload(); }, [reload, tick]);

  const n = (v: number) => (my ? toMyanmarDigits(Math.round(v * 100) / 100) : String(Math.round(v * 100) / 100));

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center gap-2">
        {(['drugs', 'low', 'expiring'] as Tab[]).map((x) => (
          <button key={x} onClick={() => setTab(x)}
            className={cx('rounded-full border px-3 py-1.5 text-sm font-semibold', tab === x ? 'border-accent bg-accent-soft text-accent-ink' : 'border-rule bg-surface text-ink-2')}>
            {t(x === 'drugs' ? 'pharmacy.drugs' : x === 'low' ? 'pharmacy.lowStock' : 'pharmacy.expiringTitle')}
            {x === 'expiring' && expiring.length > 0 && <span className="ml-1.5 rounded-full bg-warn-soft px-1.5 text-warn">{n(expiring.length)}</span>}
          </button>
        ))}
        <span className="ml-auto text-sm text-ink-2">{t('pharmacy.stockValue')}: <b>{formatMMK(value, my ? 'my' : 'en')}</b></span>
      </div>

      {tab !== 'expiring' && (
        <div className="flex flex-wrap gap-2">
          <Input className="min-w-56 flex-1" placeholder={t('pharmacy.searchDrugs')} value={q} onChange={(e) => setQ(e.target.value)} />
          <Button onClick={() => setReceiving(true)}>＋ {t('pharmacy.receive')}</Button>
          <Button onClick={() => setImporting(true)}>{t('pharmacy.import')}</Button>
          <Button variant="primary" onClick={() => setEditing({ generic_name: '', selling_price: 0 })}>＋ {t('pharmacy.addDrug')}</Button>
        </div>
      )}

      {tab === 'expiring' ? (
        expiring.length === 0 ? <Empty>—</Empty> : (
          <div className="overflow-x-auto rounded-xl border border-rule bg-surface">
            <table className="w-full text-left text-sm">
              <thead className="bg-bg text-xs uppercase tracking-wide text-muted">
                <tr><th className="px-4 py-2">{t('pharmacy.generic')}</th><th className="px-4 py-2">{t('pharmacy.batch')}</th><th className="px-4 py-2">{t('pharmacy.expiry')}</th><th className="px-4 py-2 text-right">{t('pharmacy.onHand')}</th></tr>
              </thead>
              <tbody>
                {expiring.map((b) => {
                  const d = daysToExpiry(b.expiry_on);
                  return (
                    <tr key={b.batch_id} className="border-t border-rule">
                      <td className="px-4 py-2"><b>{b.generic_name}</b> {b.strength} {b.brand_name && <span className="text-ink-2">· {b.brand_name}</span>}</td>
                      <td className="px-4 py-2 font-mono text-xs">{b.batch_no}</td>
                      <td className="px-4 py-2">{formatDate(b.expiry_on, my ? 'my' : 'en')} <Pill tone={d !== null && d < 0 ? 'danger' : 'warn'}>{d !== null && d < 0 ? t('pharmacy.expired') : t('pharmacy.expiringSoon')}</Pill></td>
                      <td className="px-4 py-2 text-right font-bold">{n(b.qty_on_hand)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      ) : rows.length === 0 ? <Empty>{t('pharmacy.noDrugs')}</Empty> : (
        <div className="overflow-x-auto rounded-xl border border-rule bg-surface">
          <table className="w-full text-left text-sm">
            <thead className="bg-bg text-xs uppercase tracking-wide text-muted">
              <tr>
                <th className="px-4 py-2">{t('pharmacy.generic')}</th><th className="px-4 py-2">{t('pharmacy.strength')}</th>
                <th className="px-4 py-2 text-right">{t('pharmacy.price')}</th><th className="px-4 py-2 text-right">{t('pharmacy.onHand')}</th>
                <th className="px-4 py-2">{t('pharmacy.expiry')}</th><th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.drug_id} className={cx('border-t border-rule', d.qty_on_hand <= d.reorder_level && 'bg-warn-soft/40')}>
                  <td className="px-4 py-2"><b>{d.generic_name}</b>{d.brand_name && <span className="text-ink-2"> · {d.brand_name}</span>}</td>
                  <td className="px-4 py-2 text-ink-2">{[d.strength, d.form].filter(Boolean).join(' ')}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{formatMMK(d.selling_price, my ? 'my' : 'en')}</td>
                  <td className="px-4 py-2 text-right font-bold tabular-nums">{n(d.qty_on_hand)} {d.qty_on_hand <= d.reorder_level && <Pill tone="warn">{t('pharmacy.lowStock')}</Pill>}</td>
                  <td className="px-4 py-2 text-ink-2">{d.nearest_expiry ? formatDate(d.nearest_expiry, my ? 'my' : 'en') : '—'}</td>
                  <td className="px-4 py-2 text-right">
                    <button className="text-xs text-ink-2 underline" onClick={() => setAdjusting(d)}>{t('pharmacy.adjust')}</button>
                    <button className="ml-3 text-xs text-ink-2 underline" onClick={() => setEditing({
                      id: d.drug_id, generic_name: d.generic_name, brand_name: d.brand_name, strength: d.strength, form: d.form,
                      dispense_unit: d.dispense_unit, selling_price: d.selling_price, reorder_level: d.reorder_level, is_consumable: d.is_consumable === 1,
                    })}>{t('common.edit')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? t('pharmacy.editDrug') : t('pharmacy.addDrug')}>
        {editing && <DrugForm initial={editing} onCancel={() => setEditing(null)} onSaved={async () => { setEditing(null); await reload(); }} />}
      </Modal>
      <Modal open={receiving} onClose={() => setReceiving(false)} title={t('pharmacy.receive')} wide>
        <ReceiveForm onCancel={() => setReceiving(false)} onSaved={async () => { setReceiving(false); await reload(); }} />
      </Modal>
      <Modal open={importing} onClose={() => setImporting(false)} title={t('pharmacy.import')} wide>
        <ImportForm onCancel={() => setImporting(false)} onDone={async () => { setImporting(false); await reload(); }} />
      </Modal>
      <Modal open={!!adjusting} onClose={() => setAdjusting(null)} title={t('pharmacy.adjust')}>
        {adjusting && <AdjustForm drug={adjusting} onCancel={() => setAdjusting(null)} onSaved={async () => { setAdjusting(null); await reload(); }} />}
      </Modal>
    </div>
  );
}

function DrugForm({ initial, onSaved, onCancel }: { initial: DrugInput; onSaved: () => void; onCancel: () => void }) {
  const { t } = useTranslation();
  const ctx = useClinicCtx();
  const [f, setF] = useState<DrugInput>(initial);
  const [busy, setBusy] = useState(false);
  const UNITS = ['tablet', 'capsule', 'ml', 'sachet', 'vial', 'ampoule', 'tube', 'bottle', 'inhaler', 'patch', 'piece'];
  return (
    <form className="grid gap-3 sm:grid-cols-2" onSubmit={async (e) => { e.preventDefault(); setBusy(true); try { await saveDrug(ctx, f); onSaved(); } finally { setBusy(false); } }}>
      <Field label={t('pharmacy.generic')} required><Input value={f.generic_name} onChange={(e) => setF({ ...f, generic_name: e.target.value })} required autoFocus /></Field>
      <Field label={t('pharmacy.brand')}><Input value={f.brand_name ?? ''} onChange={(e) => setF({ ...f, brand_name: e.target.value })} /></Field>
      <Field label={t('pharmacy.strength')}><Input value={f.strength ?? ''} onChange={(e) => setF({ ...f, strength: e.target.value })} placeholder="500 mg" /></Field>
      <Field label={t('pharmacy.form')}><Input value={f.form ?? ''} onChange={(e) => setF({ ...f, form: e.target.value })} placeholder="Tablet" /></Field>
      <Field label={t('pharmacy.unit')}>
        <Select value={f.dispense_unit ?? 'tablet'} onChange={(e) => setF({ ...f, dispense_unit: e.target.value })}>{UNITS.map((u) => <option key={u} value={u}>{u}</option>)}</Select>
      </Field>
      <Field label={t('pharmacy.price')} required><Input type="number" min={0} inputMode="numeric" value={f.selling_price} onChange={(e) => setF({ ...f, selling_price: Number(e.target.value) || 0 })} required /></Field>
      <Field label={t('pharmacy.reorder')}><Input type="number" min={0} inputMode="numeric" value={f.reorder_level ?? 0} onChange={(e) => setF({ ...f, reorder_level: Number(e.target.value) || 0 })} /></Field>
      <label className="flex items-center gap-2 self-end pb-3"><input type="checkbox" className="h-5 w-5" checked={!!f.is_consumable} onChange={(e) => setF({ ...f, is_consumable: e.target.checked })} />{t('pharmacy.consumable')}</label>
      <div className="flex justify-end gap-2 sm:col-span-2">
        <Button type="button" onClick={onCancel}>{t('common.cancel')}</Button>
        <Button type="submit" variant="primary" disabled={busy || !f.generic_name.trim()}>{t('common.save')}</Button>
      </div>
    </form>
  );
}

function ReceiveForm({ onSaved, onCancel }: { onSaved: () => void; onCancel: () => void }) {
  const { t, i18n } = useTranslation();
  const my = i18n.language === 'my';
  const ctx = useClinicCtx();
  const today = new Date().toISOString().slice(0, 10);
  const [drugs, setDrugs] = useState<DrugStock[]>([]);
  const [suppliers, setSuppliers] = useState<{ id: string; name: string }[]>([]);
  const [head, setHead] = useState({ supplier_id: '', newSupplier: '', invoice_no: '', received_on: today });
  const [lines, setLines] = useState<ReceiptLine[]>([{ drug_id: '', batch_no: '', expiry_on: '', qty: 0, unit_cost: 0 }]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void stockList(ctx.db, ctx.clinicId).then(setDrugs);
    void listSuppliers(ctx.db, ctx.clinicId).then(setSuppliers);
  }, [ctx.db, ctx.clinicId]);

  const total = lines.reduce((s, l) => s + l.qty * l.unit_cost, 0);
  const valid = lines.filter((l) => l.drug_id && l.qty > 0);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      let supplierId = head.supplier_id || null;
      if (!supplierId && head.newSupplier.trim()) supplierId = await saveSupplier(ctx, head.newSupplier.trim(), null);
      await receiveGoods(ctx, {
        supplier_id: supplierId, invoice_no: head.invoice_no || null, received_on: head.received_on,
        lines: valid.map((l) => ({ ...l, expiry_on: l.expiry_on || null })),
      });
      onSaved();
    } finally { setBusy(false); }
  }

  return (
    <form onSubmit={submit} className="grid gap-3">
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label={t('pharmacy.supplier')}>
          {suppliers.length > 0 ? (
            <Select value={head.supplier_id} onChange={(e) => setHead({ ...head, supplier_id: e.target.value })}>
              <option value="">—</option>
              {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Select>
          ) : <Input value={head.newSupplier} onChange={(e) => setHead({ ...head, newSupplier: e.target.value })} />}
        </Field>
        <Field label={t('pharmacy.invoiceNo')}><Input value={head.invoice_no} onChange={(e) => setHead({ ...head, invoice_no: e.target.value })} /></Field>
        <Field label={t('pharmacy.receivedOn')}><Input type="date" value={head.received_on} onChange={(e) => setHead({ ...head, received_on: e.target.value })} /></Field>
      </div>

      <div className="grid gap-2">
        {lines.map((l, i) => (
          <div key={i} className="grid grid-cols-2 gap-2 rounded-lg border border-rule p-2 sm:grid-cols-[2fr_1fr_1fr_1fr_1fr_auto]">
            <Select value={l.drug_id} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, drug_id: e.target.value } : x))}>
              <option value="">—</option>
              {drugs.map((d) => <option key={d.drug_id} value={d.drug_id}>{d.generic_name} {d.strength ?? ''}</option>)}
            </Select>
            <Input placeholder={t('pharmacy.batch')} value={l.batch_no} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, batch_no: e.target.value } : x))} />
            <Input type="date" value={l.expiry_on ?? ''} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, expiry_on: e.target.value } : x))} />
            <Input type="number" min={0} inputMode="numeric" placeholder={t('pharmacy.qty')} value={l.qty || ''} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, qty: Number(e.target.value) || 0 } : x))} />
            <Input type="number" min={0} inputMode="numeric" placeholder={t('pharmacy.cost')} value={l.unit_cost || ''} onChange={(e) => setLines(lines.map((x, j) => j === i ? { ...x, unit_cost: Number(e.target.value) || 0 } : x))} />
            <button type="button" className="px-2 text-ink-2 hover:text-danger" onClick={() => setLines(lines.filter((_, j) => j !== i))} aria-label="remove">✕</button>
          </div>
        ))}
        <Button type="button" onClick={() => setLines([...lines, { drug_id: '', batch_no: '', expiry_on: '', qty: 0, unit_cost: 0 }])}>＋ {t('pharmacy.addLine')}</Button>
      </div>

      <div className="flex items-center justify-between border-t border-rule pt-3">
        <span className="font-bold">{t('pharmacy.total')}: {formatMMK(total, my ? 'my' : 'en')}</span>
        <div className="flex gap-2">
          <Button type="button" onClick={onCancel}>{t('common.cancel')}</Button>
          <Button type="submit" variant="primary" disabled={busy || valid.length === 0}>{t('pharmacy.post')}</Button>
        </div>
      </div>
    </form>
  );
}

function AdjustForm({ drug, onSaved, onCancel }: { drug: DrugStock; onSaved: () => void; onCancel: () => void }) {
  const { t, i18n } = useTranslation();
  const my = i18n.language === 'my';
  const ctx = useClinicCtx();
  const [batches, setBatches] = useState<BatchStock[]>([]);
  const [f, setF] = useState({ batch_id: '', qty: '', kind: 'adjustment' as AdjustKind, reason: '' });
  const [busy, setBusy] = useState(false);
  useEffect(() => { void batchesForDrug(ctx.db, drug.drug_id).then((b) => { setBatches(b); setF((x) => ({ ...x, batch_id: b[0]?.batch_id ?? '' })); }); }, [ctx.db, drug.drug_id]);

  return (
    <form className="grid gap-3" onSubmit={async (e) => {
      e.preventDefault(); setBusy(true);
      try { await adjustStock(ctx, { drug_id: drug.drug_id, batch_id: f.batch_id, qty: Number(f.qty), kind: f.kind, reason: f.reason.trim() }); onSaved(); } finally { setBusy(false); }
    }}>
      <p className="m-0 font-semibold">{drug.generic_name} {drug.strength}</p>
      <Field label={t('pharmacy.batch')}>
        <Select value={f.batch_id} onChange={(e) => setF({ ...f, batch_id: e.target.value })} required>
          {batches.map((b) => <option key={b.batch_id} value={b.batch_id}>{b.batch_no || '—'} · {t('pharmacy.onHand')} {my ? toMyanmarDigits(b.qty_on_hand) : b.qty_on_hand}{b.expiry_on ? ` · ${formatDate(b.expiry_on, my ? 'my' : 'en')}` : ''}</option>)}
        </Select>
      </Field>
      <Field label={t('pharmacy.adjustKind')}>
        <Select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value as AdjustKind })}>
          {(['adjustment', 'damage', 'expiry_writeoff'] as AdjustKind[]).map((k) => <option key={k} value={k}>{t(`pharmacy.${k}`)}</option>)}
        </Select>
      </Field>
      <Field label={t('pharmacy.adjustQty')} hint={t('pharmacy.adjustHint')}>
        <Input inputMode="numeric" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} required />
      </Field>
      <Field label={t('pharmacy.reason')} required><Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} required /></Field>
      <div className="flex justify-end gap-2">
        <Button type="button" onClick={onCancel}>{t('common.cancel')}</Button>
        <Button type="submit" variant="primary" disabled={busy || !f.batch_id || !f.reason.trim() || !Number(f.qty)}>{t('common.save')}</Button>
      </div>
    </form>
  );
}

function ImportForm({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const { t } = useTranslation();
  const ctx = useClinicCtx();
  const [parsed, setParsed] = useState<ParsedDrug[] | null>(null);
  const [busy, setBusy] = useState(false);
  const ok = (parsed ?? []).filter((r) => r.errors.length === 0);
  const bad = (parsed ?? []).filter((r) => r.errors.length > 0);

  async function pick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setParsed(parseDrugCsv(await file.text()).rows);
  }

  async function commit() {
    setBusy(true);
    try {
      for (const r of ok) {
        await saveDrug(ctx, {
          generic_name: r.generic_name, brand_name: r.brand_name, manufacturer: r.manufacturer,
          strength: r.strength, form: r.form, dispense_unit: r.dispense_unit,
          selling_price: r.selling_price, reorder_level: r.reorder_level,
        });
      }
      onDone();
    } finally { setBusy(false); }
  }

  return (
    <div className="grid gap-3">
      <p className="m-0 text-sm text-muted">{t('pharmacy.importHint')}</p>
      <input type="file" accept=".csv,.tsv,.txt,text/csv" onChange={pick} className="rounded-lg border border-rule bg-surface p-2" />
      {parsed && (
        <>
          <p className="m-0 font-semibold">{t('pharmacy.importPreview', { ok: ok.length, bad: bad.length })}</p>
          <div className="max-h-60 overflow-y-auto rounded-lg border border-rule">
            <table className="w-full text-left text-sm">
              <tbody>
                {ok.slice(0, 50).map((r) => (
                  <tr key={r.row} className="border-b border-rule">
                    <td className="px-3 py-1.5"><b>{r.generic_name}</b> {r.brand_name && <span className="text-ink-2">· {r.brand_name}</span>} {r.strength}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{r.selling_price.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {bad.length > 0 && (
            <details className="text-sm">
              <summary className="cursor-pointer font-semibold text-warn">{t('pharmacy.importErrors')} ({bad.length})</summary>
              <ul className="mt-1 list-disc pl-5 text-ink-2">
                {bad.slice(0, 20).map((r) => <li key={r.row}>{t('common.loading') && `#${r.row}`} {r.generic_name || '—'}: {r.errors.join(', ')}</li>)}
              </ul>
            </details>
          )}
        </>
      )}
      <div className="flex justify-end gap-2">
        <Button type="button" onClick={onCancel}>{t('common.cancel')}</Button>
        <Button variant="primary" disabled={busy || ok.length === 0} onClick={commit}>{t('pharmacy.importConfirm', { n: ok.length })}</Button>
      </div>
    </div>
  );
}
